import { ActionIcon, Menu, Text } from "@mantine/core"
import {
  IconDownload,
  IconTableImport,
  IconQrcode,
  IconDotsVertical,
  IconFileArrowLeft,
  IconFolderOff,
  IconRefresh,
} from "@tabler/icons-react"
import { DocumentDownload } from "@tfg/ui"

import { FindIngestionsResponseDto } from "~/gen/api"
import { fetchDocumentUrl } from "~/hooks/useDocumentsQuery"
import { useRetryAction } from "~/pages/Admin/Ingestion/ProductImport/hooks/useRetryAction"

type IngestionLaunchMenuProps = {
  row: {
    original: FindIngestionsResponseDto
  }
  handleErrorModalOpen: (ingestionId: string) => void
  handleImportModalOpen: (
    ingestion: FindIngestionsResponseDto,
    isBlankIngestion: boolean
  ) => void
  revertImport: (params: { data: { ingestionId: string } }) => void
  exportProductQrCode: (params: { data: { ingestionId: string } }) => void
}

export const IngestionLaunchMenu = ({
  row,
  handleErrorModalOpen,
  handleImportModalOpen,
  revertImport,
  exportProductQrCode,
}: IngestionLaunchMenuProps) => {
  const getRetryAction = useRetryAction({
    handleImportModalOpen,
  })

  const retryAction = getRetryAction(row.original)

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon>
          <IconDotsVertical />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {row.original.status === "PRODUCTS_INGESTED" &&
        !row.original.isTraceabilityLaunched ? (
          <Menu.Item
            onClick={() =>
              revertImport({ data: { ingestionId: row.original.id } })
            }
            leftSection={<IconFolderOff />}
          >
            <Text fz="sm">Revert Import</Text>
          </Menu.Item>
        ) : null}

        {row.original.status === "BLANK_PRODUCTS_INGESTED" ? (
          <>
            <Menu.Item
              onClick={() =>
                exportProductQrCode({
                  data: { ingestionId: row.original.id },
                })
              }
              leftSection={<IconQrcode />}
            >
              <Text fz="sm">Export Qr Code</Text>
            </Menu.Item>
          </>
        ) : null}

        {row.original.status === "ERROR" ? (
          <>
            <Menu.Item
              leftSection={<IconFileArrowLeft />}
              onClick={() => handleErrorModalOpen(row.original.id)}
            >
              <Text fz="sm">Show error logs</Text>
            </Menu.Item>
          </>
        ) : null}

        {row.original.status === "BLANK_PRODUCTS_VERIFIED" ? (
          <Menu.Item
            leftSection={<IconTableImport />}
            onClick={() => handleImportModalOpen(row.original, true)}
          >
            <Text fz="sm">Import blank products</Text>
          </Menu.Item>
        ) : null}

        {row.original.status === "PRODUCTS_VERIFIED" ? (
          <Menu.Item
            leftSection={<IconTableImport />}
            onClick={() => handleImportModalOpen(row.original, false)}
          >
            <Text fz="sm">Import products</Text>
          </Menu.Item>
        ) : null}

        {["ERROR", "FAILED"].includes(row.original.status) &&
        row.original.step &&
        retryAction ? (
          <>
            <Menu.Item
              leftSection={<IconRefresh />}
              onClick={() => retryAction.trigger(row.original)}
            >
              <Text fz="sm">{retryAction.name}</Text>
            </Menu.Item>
          </>
        ) : null}

        <Menu.Item leftSection={<IconDownload />}>
          <DocumentDownload
            documentName={row.original.document.originalFileName ?? ""}
            showAsText={true}
            customText="Download product import file"
            fetchDownloadUrl={() =>
              fetchDocumentUrl(row.original.document.id, true)
            }
          />
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}
